import {
  IBulkBatch,
  IBulkPayableBatch
} from "@wingspanhq/payments/dist/interfaces";
import { hybridEncrypt } from "@wingspanhq/utils/dist/crypto";
import { useMemo } from "react";

import { useAccountAccess } from "@hooks/useAccountAccess";
import { useRouteMatch } from "react-router-dom";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { useQueryUserProfile } from "../../../query/users/queries";
import { pullSessionToken } from "../../../services/sessionStorage";
import { IS_DEV_ENV } from "../../../shared/constants/environment";
import { themeConfig } from "../../../shared/flatfile/themeConfig";
import { useNonContractorXWingspanExpansion } from "../../../shared/hooks/useXWingspanExpansion";
import { useXWingspanPayerId } from "../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import {
  BULK_IMPORT_CONTRACTORS_NAMESPACE,
  BULK_IMPORT_PAYABLES_NAMESPACE
} from "../constants";
import { BulkResource } from "../types";
import { getBatchCreatorName } from "../utils/getBatchCreator";

export function useFlatfileSpaceConfig(
  bulkBatch: IBulkBatch,
  bulkResource: BulkResource
) {
  const match = useRouteMatch<{ bulkBatchId: string }>();
  const { bulkBatchId } = match.params;

  const userId = useUserId();
  const queryUser = useQueryUserProfile(userId);

  const queryFeatureFlags = useQueryFeatureFlags();

  const xWingspanExpansion = useNonContractorXWingspanExpansion();
  const xWingspanPayerId = useXWingspanPayerId();

  const { isImpersonatedByWingspanAdmin } = useAccountAccess();

  const spaceProps = useMemo(() => {
    // encrypt the API token for storage in the metadata
    const publicKey = process.env.REACT_APP_ENCRYPTION_PUBLIC_KEY as string;
    const originalPublicKey = publicKey.replace(/\\n/g, "\n");

    const apiTokenEncrypted = hybridEncrypt(
      originalPublicKey,
      pullSessionToken() as string
    );

    let spaceConfig = {
      name: "Payables Upload",
      namespace: BULK_IMPORT_PAYABLES_NAMESPACE
    };
    if (bulkResource === BulkResource.Collaborator) {
      spaceConfig = {
        name: "Contractors Upload",
        namespace: BULK_IMPORT_CONTRACTORS_NAMESPACE
      };
    }

    return {
      ...(bulkBatch?.labels.flatfileBatchId && {
        id: bulkBatch.labels.flatfileBatchId as string
      }),
      config: {
        ...spaceConfig,

        // set batch Id
        // set api token to use it on listener
        metadata: {
          // custom data
          apiToken: apiTokenEncrypted,
          customHeaders: {
            ...(xWingspanExpansion && {
              "x-wingspan-expansion": xWingspanExpansion
            }),
            ...(xWingspanPayerId && { "x-wingspan-user": xWingspanPayerId })
          },
          bulkBatchId,

          isEngagementsEnabled: queryFeatureFlags.data?.flatfileEngagements,
          // user info
          userInfo: {
            // FIXME: https://linear.app/wingspan/issue/WIN-11396
            userId: (bulkBatch as IBulkPayableBatch)?.clientId,
            name: getBatchCreatorName(bulkBatch as IBulkPayableBatch)
          },

          // part of configuration
          sidebarConfig: {
            // show sidebar for impersonated wingspan admin
            showSidebar: isImpersonatedByWingspanAdmin || IS_DEV_ENV
          },
          ...themeConfig
        }
      }
    };
  }, [
    bulkBatch,
    bulkBatchId,
    bulkResource,
    isImpersonatedByWingspanAdmin,
    queryFeatureFlags.data?.flatfileEngagements,
    xWingspanExpansion,
    xWingspanPayerId
  ]);

  return spaceProps;
}
